import React, { useState, useEffect } from 'react';
import { MessageCircle, Sparkles } from 'lucide-react';

const WHATSAPP = '918889616972';

const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined') {
    // Google Analytics 4
    if (window.gtag) {
      window.gtag('event', eventName, params);
    }
    // Meta Pixel
    if (window.fbq) {
      window.fbq('trackCustom', eventName, params);
    }
    // Console log for debugging
    console.log('[Analytics]', eventName, params);
  }
};

const StickyCTA = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling 200px
      const scrolled = window.scrollY > 200;
      if (scrolled !== isVisible) {
        setIsVisible(scrolled);
        if (scrolled) setIsAnimating(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Check on mount
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isVisible]);

  const handleClick = () => {
    trackEvent('sticky_cta_click', {
      button_text: 'Order via WhatsApp',
      location: 'sticky_cta',
    });
    const message = `Hi AuraPrintiqo! 👋\n\nI want to customize a product.\n\nCould you please guide me on how to get started?`;
    window.open(
      `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(message)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const scrollToSection = () => {
    trackEvent('sticky_cta_click', {
      button_text: 'Customize Now',
      location: 'sticky_desktop_float',
    });
    // Try to find the collections section or scroll to it
    const el = document.getElementById('collections-section') || document.querySelector('[data-section="how-it-works"]');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      handleClick();
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Mobile: Sticky bottom bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-all duration-300 ${
          isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="bg-white border-t border-gray-100 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] px-4 py-3">
          <button
            onClick={handleClick}
            className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl text-white font-semibold text-base shadow-lg active:scale-95 transition-transform"
            style={{
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              minHeight: '52px',
            }}
          >
            <MessageCircle className="w-5 h-5 flex-shrink-0" />
            <span>Order via WhatsApp</span>
            <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium">Free</span>
          </button>
          <p className="text-center text-xs text-gray-400 mt-2">
            🎨 Artwork preview shared before printing • ✅ 2 free revisions
          </p>
        </div>
      </div>

      {/* Desktop: Floating pill button */}
      <div
        className={`fixed bottom-8 right-8 z-50 hidden md:flex flex-col gap-3 items-end transition-all duration-300 ${
          isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
        }`}
      >
        {/* WhatsApp float */}
        <button
          onClick={handleClick}
          title="Order via WhatsApp"
          className="group flex items-center gap-3 px-5 py-3 rounded-full text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95"
          style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
        >
          <MessageCircle className="w-5 h-5" />
          <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
            Order via WhatsApp
          </span>
        </button>

        {/* Customize Now float */}
        <button
          onClick={scrollToSection}
          className="flex items-center gap-2 px-5 py-3 rounded-full text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95"
          style={{
            background: 'linear-gradient(135deg, hsl(350,70%,65%) 0%, hsl(20,90%,70%) 100%)',
          }}
        >
          <Sparkles className="w-5 h-5" />
          <span>Customize Now</span>
        </button>
      </div>
    </>
  );
};

export default StickyCTA;
